import { useState } from "react"

import { Link, useNavigate } from "react-router-dom"

import {
  batteryConcertErrorCode,
  concertInitiationErrorMessage,
} from "~/api/battery-concert-application-errors"
import type {
  BatteryProposalDto,
  BatteryQuoteDto,
} from "~/api/dto/battery-proposal.dto"
import useBatteryConcertApplication from "~/api/hooks/useBatteryConcertApplication"
import useInitiateBatteryConcertApplication from "~/api/hooks/useInitiateBatteryConcertApplication"

import { isBatteryProposalReadOnly } from "../../battery-proposal-read-only"
import { RESERVE_NOW_LABEL } from "../../contract/contract-action"
import { SelectLoanProductAction } from "../../select-loan-product-action"
import {
  batteryContractPath,
  batteryProposalPath,
} from "../../setup/setup-steps"
import { APPLICATION_STATUS_ACTION_LABEL } from "../application-status-labels"
import type { BatteryDrawerLaunchState } from "../battery-drawer-launch"
import type { ConcertBatteryApplicantPrefill } from "./ConcertBatteryApplicationForm"
import { ConcertBatteryApplicationForm } from "./ConcertBatteryApplicationForm"
import { useBatteryConcertApplicationNotifications } from "./use-battery-concert-application-notifications"

export const SETTING_UP_CUSTOMER_LABEL = `Setting up customer`

type FormContext = {
  quoteId: string
  prefill: ConcertBatteryApplicantPrefill
}

const prefillFromProposal = (
  proposal: BatteryProposalDto,
): ConcertBatteryApplicantPrefill => ({
  firstName: proposal.homeowner?.firstName ?? ``,
  lastName: proposal.homeowner?.lastName ?? ``,
  email: proposal.homeowner?.email ?? ``,
  phone: proposal.homeowner?.phone ?? ``,
  street: proposal.address.street,
  city: proposal.address.city,
  state: proposal.address.state,
  zip: proposal.address.postal,
})

const pillClass = `font-display inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-[length:var(--step-0)] font-bold text-bp-accent shadow-[0_12px_32px_rgba(0,0,0,0.25)] hover:bg-white/90 focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60`

/**
 * Reserve now for a Concert Battery plan, beneath the Financial Summary
 * ledger. The click asks the gateway to create or reuse the Proposal's
 * Concert customer; the application form opens only once that is confirmed.
 * After any Existing Loan Application the entry becomes Application status,
 * which opens Contract status. Nothing here runs credit.
 */
export function ConcertBatteryReserveNow({
  proposal,
  quote,
  needsLoanProduct,
}: {
  proposal: BatteryProposalDto
  quote: BatteryQuoteDto | null
  needsLoanProduct: boolean
}): JSX.Element | null {
  const proposalId = proposal.id
  const navigate = useNavigate()
  const statusQuery = useBatteryConcertApplication(proposalId)
  const initiate = useInitiateBatteryConcertApplication()
  const [form, setForm] = useState<FormContext | null>(null)
  useBatteryConcertApplicationNotifications(proposalId)

  const openStatus = (): void => {
    navigate(batteryContractPath(proposalId), {
      state: {
        returnTo: batteryProposalPath(proposalId),
      } satisfies BatteryDrawerLaunchState,
    })
  }

  const status = statusQuery.data
  if (status == null) {
    return statusQuery.isError ? (
      <p
        role="alert"
        data-testid="battery-concert-status-unavailable"
        className="mt-6 text-[length:var(--step--2)] text-white"
      >
        Concert application status is unavailable right now.
      </p>
    ) : null
  }

  if (status.application != null) {
    return (
      <div
        data-testid="battery-existing-loan-application"
        className="relative mt-6 border-t border-white/25 pt-6 print:hidden"
      >
        <Link
          to={batteryContractPath(proposalId)}
          state={
            {
              returnTo: batteryProposalPath(proposalId),
            } satisfies BatteryDrawerLaunchState
          }
          data-testid="battery-concert-application-status-link"
          className={pillClass}
        >
          {APPLICATION_STATUS_ACTION_LABEL}
          <span aria-hidden="true">→</span>
        </Link>
      </div>
    )
  }

  if (isBatteryProposalReadOnly(proposal)) {
    return null
  }

  if (needsLoanProduct) {
    return <SelectLoanProductAction proposalId={proposalId} />
  }

  const quoteId = quote?.id ?? null
  const canInitiate = status.actions.initiate.allowed && quoteId != null
  const pending = initiate.isLoading
  const reason = canInitiate
    ? null
    : (status.actions.initiate.reason ??
      `Refresh Payment and select a current Concert Battery Quote`)

  const onReserve = (): void => {
    if (!canInitiate || pending || quoteId == null) {
      return
    }
    initiate.mutate(
      { proposalId, body: { quoteId } },
      {
        onSuccess: (context) => {
          const fallback = prefillFromProposal(proposal)
          setForm({
            quoteId: context.quoteId,
            prefill: {
              firstName: context.customer.cfirstname || fallback.firstName,
              lastName: context.customer.clastname || fallback.lastName,
              email: context.customer.cemail ?? fallback.email,
              phone: context.customer.cphone1 ?? fallback.phone,
              street: context.customer.caddress1 || fallback.street,
              city: context.customer.ccity || fallback.city,
              state: context.customer.cstate || fallback.state,
              zip: context.customer.czip || fallback.zip,
            },
          })
        },
        onError: (error) => {
          if (batteryConcertErrorCode(error) === `CONCERT_APPLICATION_EXISTS`) {
            openStatus()
          }
        },
      },
    )
  }

  // Submitted, uncertain, or already-existing: the status drawer explains it.
  const onSubmitted = (): void => {
    setForm(null)
    openStatus()
  }

  return (
    <div
      data-testid="battery-concert-reserve-now"
      className="relative mt-7 flex flex-col items-start gap-3 print:hidden"
    >
      {initiate.isError ? (
        <p
          role="alert"
          data-testid="battery-concert-initiate-error"
          className="max-w-md text-[length:var(--step--2)] text-white"
        >
          {concertInitiationErrorMessage(initiate.error)}
        </p>
      ) : null}
      <button
        type="button"
        disabled={!canInitiate || pending}
        onClick={onReserve}
        className={pillClass}
      >
        {pending ? SETTING_UP_CUSTOMER_LABEL : RESERVE_NOW_LABEL}
        <span aria-hidden="true">→</span>
      </button>
      {reason != null && !pending ? (
        <div className="flex flex-col items-start gap-2">
          <p
            data-testid="battery-concert-reserve-now-reason"
            className="text-[length:var(--step--2)] text-white/85"
          >
            {reason}
          </p>
        </div>
      ) : null}
      {form != null ? (
        <ConcertBatteryApplicationForm
          proposalId={proposalId}
          quoteId={form.quoteId}
          prefill={form.prefill}
          onClose={() => setForm(null)}
          onSubmitted={onSubmitted}
        />
      ) : null}
    </div>
  )
}
